import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, Select, MenuItem, Typography, makeStyles } from '@material-ui/core';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { AtCircularProgress } from '../../../components/AtCircularProgress';
import { useBenchmarkFilters } from '../../../hooks/useBenchmarkFilters';
import { useBenchmarkLite } from '../../../hooks/useBenchmarkLite';

const useStyles = makeStyles(() => ({
  filtersRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: '120px', 
  },
  label: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: '#6B7280',
    marginLeft: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  select: {
    width: '100%',
    '& .MuiSelect-select': {
      padding: '6px 12px',
      fontSize: '0.8rem',
      backgroundColor: '#F9FAFB',
      borderRadius: '20px',
      fontWeight: 600,
      color: '#374151',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid #E5E7EB',
      borderRadius: '20px',
    },
  },
}));

function decodeHtmlEntities(str) {
  if (!str) return str;
  try {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  } catch {
    return str;
  }
}

export const BenchmarkPanel = ({ objectiveId }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { data: filtersResponse, isLoading: isLoadingFilters, isError: isFiltersError, refetch: refetchFilters } = useBenchmarkFilters();
  const clienteName = filtersResponse?.data?.cliente || '';

  const filterData = useMemo(() => {
    try {
      return filtersResponse?.data?.filtros || {};
    } catch {
      return {};
    }
  }, [filtersResponse]);

  const availableCanales = useMemo(() => {
    try {
      const keys = Object.keys(filterData);
      return Array.isArray(keys) ? keys : [];
    } catch {
      return [];
    }
  }, [filterData]);
  
  const [selectedCanal, setSelectedCanal] = useState('');
  const [selectedTipoServicio, setSelectedTipoServicio] = useState('');
  const [selectedMarkerIdx, setSelectedMarkerIdx] = useState(0);

  useEffect(() => {
    if (availableCanales.length > 0 && !selectedCanal) {
      const firstCanal = availableCanales[0];
      setSelectedCanal(firstCanal);
      
      const servicios = Object.keys(filterData[firstCanal] || {});
      if (servicios.length > 0) {
        setSelectedTipoServicio(servicios[0]);
      }
    }
  }, [availableCanales, filterData, selectedCanal]);

  const availableServicios = useMemo(() => {
    if (!selectedCanal || !filterData[selectedCanal]) return [];
    try {
      return Object.keys(filterData[selectedCanal]);
    } catch {
      return [];
    }
  }, [selectedCanal, filterData]);

  useEffect(() => {
    if (selectedCanal && availableServicios.length > 0 && !availableServicios.includes(selectedTipoServicio)) {
      setSelectedTipoServicio(availableServicios[0]);
    }
  }, [selectedCanal, availableServicios, selectedTipoServicio]);

  const fechaRange = useMemo(() => {
    const now = new Date();
    const inicio = new Date();
    inicio.setDate(now.getDate() - 30);

    const format = (d) => d.toISOString().split('T')[0];

    return {
      fechaInicio: format(inicio),
      fechaTermino: format(now)
    };
  }, []);

  const densityQuery = useBenchmarkLite(
    objectiveId, 
    selectedCanal, 
    selectedTipoServicio, 
    fechaRange,
    { enabled: !!selectedCanal && !!selectedTipoServicio }
  );

  const densityResponse = densityQuery.data;
  const isFetchingDensity = densityQuery.isFetching;
  const isDensityError = densityQuery.isError;
  const isDensitySettled = !densityQuery.isFetching && (densityQuery.isSuccess || densityQuery.isError);

  const data = densityResponse?.data || null;

  const density = useMemo(() => {
    try {
      if (!data?.density) return [];
      const xArr = Array.isArray(data.density.x) ? data.density.x : [];
      const yArr = Array.isArray(data.density.y) ? data.density.y : [];
      if (xArr.length === 0 || yArr.length === 0) return [];
      const len = Math.min(xArr.length, yArr.length);
      const points = [];
      for (let i = 0; i < len; i++) {
        const x = Number(xArr[i]);
        const y = Number(yArr[i]);
        if (!isNaN(x) && !isNaN(y)) {
          points.push({ x, y });
        }
      }
      return points;
    } catch {
      return [];
    }
  }, [data]);

  const markers = useMemo(() => {
    try {
      if (!Array.isArray(data?.markers)) return [];
      return data.markers
        .filter(m => m && typeof m.x === 'number' && typeof m.y === 'number')
        .map(m => ({
          ...m,
          aplicativo: decodeHtmlEntities(m.aplicativo) || 'N/A',
          percentil: m.percentil ?? '-',
        }));
    } catch {
      return [];
    }
  }, [data]);

  const meta = useMemo(() => {
    try {
      if (!data?.meta) return null;
      return {
        promedio: typeof data.meta.promedio === 'number' ? data.meta.promedio : null,
        posicion_percentil: typeof data.meta.posicion_percentil === 'number' ? data.meta.posicion_percentil : null,
        canal: data.meta.canal || '',
        tipo_servicio: data.meta.tipo_servicio || '',
      };
    } catch {
      return null;
    }
  }, [data]);

  const isDataAvailable = density.length > 0;
  const filtersLoaded = !isLoadingFilters && filtersResponse != null;
  const filtersEmpty = filtersLoaded && availableCanales.length === 0;
  const isChartLoading = isLoadingFilters || (isFetchingDensity && !isDataAvailable);
  const isLoadError = !isDataAvailable && !isLoadingFilters && !isFetchingDensity && ((isFiltersError && !filtersLoaded) || isDensityError);
  const showNoData = !isLoadError && (filtersEmpty || (!isDataAvailable && !isLoadingFilters && !isFetchingDensity && isDensitySettled));

  const handleRetry = () => {
    if (isFiltersError) refetchFilters();
    if (isDensityError) densityQuery.refetch();
  };

  useEffect(() => {
    setSelectedMarkerIdx(0);
  }, [markers]);

  const highlightedMarker = useMemo(() => {
    if (markers.length === 0) return null;
    return markers[selectedMarkerIdx] || markers[0] || null;
  }, [markers, selectedMarkerIdx]);
  const fallbackCurve = useMemo(() => {
    const points = [];
    for (let x = 0; x <= 10; x += 0.25) {
      points.push({ x, y: Math.exp(-0.5 * Math.pow((x - 5) / 2, 2)) });
    }
    return points;
  }, []);

  if (isChartLoading) {
    return (
      <Box position={'relative'} style={{ margin: 'auto' }}>
        <AtCircularProgress />
      </Box>
    );
  }

  const curveData = isDataAvailable ? density : fallbackCurve;
  const allXValues = curveData.map(p => p.x).filter(v => typeof v === 'number' && !isNaN(v) && v >= 0);
  const maxX = allXValues.length > 0 ? Math.max(...allXValues) + 1 : 10;
  const maxTick = Math.ceil(maxX / 5) * 5 || 10;
  const ticks = [];
  for (let i = 0; i <= maxTick; i += 5) ticks.push(i);

  const plotLines = [];
  if (meta?.promedio != null && typeof meta.promedio === 'number') {
    plotLines.push({
      value: meta.promedio,
      color: '#9CA3AF',
      dashStyle: 'Dash',
      width: 1,
      zIndex: 5,
      label: {
        text: `${t('home.benchmark.average', { defaultValue: 'Promedio' })} ${meta.promedio}s`,
        align: 'center',
        textAlign: 'center',
        rotation: 0,
        style: { color: '#9CA3AF', fontSize: '11px', fontWeight: '500' },
      }
    });
  }

  const chartOptions = {
    chart: {
      type: 'areaspline',
      height: 150,
      backgroundColor: 'transparent',
      marginTop: 20,
      spacingBottom: 0,
      style: { fontFamily: 'inherit' }
    },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      type: 'linear',
      min: 0,
      max: maxX,
      tickPositions: ticks,
      labels: {
        format: '{value}s',
        style: { color: '#9CA3AF', fontSize: '11px' }
      },
      gridLineWidth: 0,
      lineWidth: 1,
      lineColor: '#E5E7EB',
      tickWidth: 0,
      plotLines: plotLines,
    },
    yAxis: {
      visible: false,
      min: 0
    },
    legend: { enabled: false },
    tooltip: {
      useHTML: true,
      backgroundColor: '#111827',
      borderColor: '#111827',
      borderRadius: 6,
      shadow: true,
      padding: 0,
      style: { color: '#FFFFFF' },
      formatter: function () {
        if (this.point && this.point.isMarkerPoint && this.point.markerData) {
          const md = this.point.markerData;
          return `<div style="padding: 6px 10px; font-size: 11px; font-family: inherit;">
            <strong style="color: #A78BFA">${md.aplicativo || 'N/A'}</strong><br/>
            Percentil: <strong>P${md.percentil ?? '-'}</strong><br/>
            Tiempo: <strong>${md.x ?? '-'}s</strong>
          </div>`;
        }
        return false;
      }
    },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(88, 28, 135, 0.25)'],
            [1, 'rgba(88, 28, 135, 0)']
          ]
        },
        lineColor: '#581C87',
        lineWidth: 2,
        marker: {
          enabled: false,
          states: { hover: { enabled: true, radius: 5 } }
        }
      }
    },
    series: [
      {
        type: 'areaspline',
        zIndex: 1,
        data: curveData.map(p => ({
          x: p.x,
          y: p.y,
          marker: { enabled: false },
          states: { hover: { halo: null } }
        }))
      },
      ...(isDataAvailable && markers.length > 0 ? [{
        type: 'scatter',
        zIndex: 5,
        enableMouseTracking: true,
        data: markers
          .filter((_, idx) => idx !== selectedMarkerIdx)
          .map(m => ({
            x: m.x,
            y: m.y,
            isMarkerPoint: true,
            markerData: m,
            marker: {
              enabled: true,
              radius: 3.5,
              fillColor: '#E5E7EB',
              lineWidth: 1.5,
              lineColor: '#9CA3AF',
              symbol: 'circle',
              states: { hover: { radius: 5 } }
            }
          }))
      }] : []),

      ...(isDataAvailable && highlightedMarker ? [{
        type: 'scatter',
        zIndex: 10,
        enableMouseTracking: true,
        data: [{
          x: highlightedMarker.x,
          y: highlightedMarker.y,
          isMarkerPoint: true,
          markerData: highlightedMarker,
          marker: {
            enabled: true,
            radius: 6.5,
            fillColor: '#7C3AED',
            lineWidth: 2,
            lineColor: '#FFFFFF',
            symbol: 'circle',
            states: { hover: { radius: 8 } }
          }
        }]
      }] : [])
    ]
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box className={classes.filtersRow}>
          {availableCanales.length > 0 && (
            <Box className={classes.selectContainer}>
              <Typography className={classes.label}>{t('home.benchmark.canal', { defaultValue: 'Canal' })}</Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={selectedCanal}
                  onChange={(e) => setSelectedCanal(e.target.value)}
                  className={classes.select}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250, borderRadius: '12px' } } }}
                >
                  {availableCanales.map(canal => (
                    <MenuItem key={canal} value={canal} style={{ fontSize: '0.85rem' }}>{canal}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {availableServicios.length > 0 && (
            <Box className={classes.selectContainer}>
              <Typography className={classes.label}>{t('home.benchmark.tipoServicio', { defaultValue: 'Servicio' })}</Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={selectedTipoServicio}
                  onChange={(e) => setSelectedTipoServicio(e.target.value)}
                  className={classes.select}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250, borderRadius: '12px' } } }}
                >
                  {availableServicios.map(serv => (
                    <MenuItem key={serv} value={serv} style={{ fontSize: '0.85rem' }}>{serv}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {markers.length > 0 && (
            <Box className={classes.selectContainer}>
              <Typography className={classes.label}>{t('home.benchmark.app', { defaultValue: 'Aplicativo a Destacar' })}</Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={selectedMarkerIdx}
                  onChange={(e) => setSelectedMarkerIdx(e.target.value)}
                  className={classes.select}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250, borderRadius: '12px' } } }}
                >
                  {markers.map((m, idx) => (
                    <MenuItem key={idx} value={idx} style={{ fontSize: '0.85rem' }}>{m.aplicativo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

      <Box position="relative" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          style={showNoData || isLoadError ? { filter: 'blur(8px)', opacity: 0.6, pointerEvents: 'none' } : {}}
        >

        {isFetchingDensity ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ flex: 1, minHeight: '130px' }}>
            <AtCircularProgress size={24} />
          </Box>
        ) : (
          <>
            {highlightedMarker && (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '4px' }}>
                <Typography variant="body2" style={{ color: '#4B5563', fontSize: '11px' }}>
                  {t('home.benchmark.position', { defaultValue: 'Posición' })}: <strong>P{highlightedMarker.percentil}</strong> — {clienteName}
                </Typography>
              </Box>
            )}

            <Box style={{ flex: 1, minHeight: '130px' }}>
              <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            </Box>
          </>
        )}

        {!isFetchingDensity && highlightedMarker && (
          <Box style={{ marginTop: '6px', paddingRight: '4px' }}>
            <Box
              p={1}
              style={{
                backgroundColor: '#F8FAFC',
                borderRadius: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                <Box style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#7C3AED', flexShrink: 0 }} />
                <Typography variant="body2" style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                  {highlightedMarker.aplicativo}
                </Typography>
              </Box>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#581C87' }}>
                P{highlightedMarker.percentil}
              </Typography>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#581C87' }}>
                {highlightedMarker.x}s
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {(showNoData || isLoadError) && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          style={{ zIndex: 10, padding: '20px', textAlign: 'center' }}
        >
          <Typography variant="button" style={{
            fontWeight: 800,
            backgroundColor: 'rgba(238, 242, 255, 0.9)',
            padding: '8px 16px',
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(67, 56, 202, 0.2)',
            letterSpacing: '1.5px'
          }}>
            {isLoadError
              ? t('text.load-error')
              : t('home.benchmark.notAvailable', { defaultValue: 'No disponible' })}
          </Typography>
          {isLoadError ? (
            <Button color="primary" onClick={handleRetry} style={{ marginTop: '8px' }}>
              {t('text.retry')}
            </Button>
          ) : (
            <Typography variant="caption" style={{ fontWeight: 600, marginTop: '8px' }}>
              {t('home.benchmark.contactExecutive', { defaultValue: 'Contactar a tu ejecutivo' })}
            </Typography>
          )}
        </Box>
      )}
      </Box>
    </Box>
  );
};